import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useClientId } from "@/shared/hooks/useClientId";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "@/shared/ui/breadcrumb";
import { Card, CardContent } from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";
import { getQueue, getVariant, getProgress, submitLabel } from "../api";
import type { SubmitBody } from "../types";
import { FindingCard } from "../components/FindingCard";
import { ScoreHeader } from "../components/ScoreHeader";

export default function LearningPage() {
  const { clientId, loading: clientLoading } = useClientId();
  const [idx, setIdx] = useState(0);

  const variantQ = useQuery({ queryKey: ["learning-variant"], queryFn: getVariant });
  // The server draws a fresh random sample + shuffle on EVERY fetch, so a
  // background refetch (window focus, reconnect) would swap the card under the
  // user mid-judgment. Fetch once and keep the session's queue stable.
  const queueQ = useQuery({
    queryKey: ["learning-queue", clientId],
    enabled: !!clientId,
    queryFn: () => getQueue(clientId as string),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const progressQ = useQuery({ queryKey: ["learning-progress"], queryFn: getProgress });

  const variant = variantQ.data ?? "B"; // default B (blind) until resolved
  const items = queueQ.data ?? [];
  const current = items[idx];

  const onSubmit = async (body: SubmitBody) => {
    const r = await submitLabel(body);
    void progressQ.refetch();
    return r;
  };

  return (
    <div className="flex h-screen min-h-0 flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b border-border bg-card/50 px-6 py-4 backdrop-blur-sm">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/dashboard">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>Learning</BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <ScoreHeader progress={progressQ.data} />
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-6">
        <div className="mx-auto max-w-2xl">
          {/* clientId resolves via an async lookup; while it (or the queue it
              gates) is loading, show the skeleton — NOT the empty state, which
              would otherwise flash on every load. */}
          {clientLoading || queueQ.isLoading ? (
            <Skeleton className="h-72 w-full rounded-xl" />
          ) : !clientId ? (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-lg font-medium text-foreground">We couldn't load your account</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  No client organization is linked to this login. If that's unexpected, contact your Robotic Imaging
                  admin.
                </p>
              </CardContent>
            </Card>
          ) : queueQ.error ? (
            <p className="text-destructive">Couldn't load the queue: {(queueQ.error as Error).message}</p>
          ) : !current ? (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-lg font-medium text-foreground">You're all caught up! 🎉</p>
                <p className="mt-1 text-sm text-muted-foreground">More evidence is on the way — check back soon.</p>
              </CardContent>
            </Card>
          ) : (
            <FindingCard
              key={current.id}
              item={current}
              variant={variant}
              onSubmit={onSubmit}
              onNext={() => setIdx((i) => i + 1)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
